import { service } from '@ember/service'
import Component from '@glimmer/component'
import { isFinite } from 'lodash'
import { formatNumber } from 'teacher-ui-client/utils/giraffe/formatter'
import { reportingToolTip as toolTipConfig } from 'teacher-ui-client/utils/giraffe/highcharts-config'
import { colors } from '@colors'
import type GradeSetsService from 'teacher-ui-client/services/grade-sets'
import { t } from 'ember-intl'
import type { SeriesColumnrangeOptions, TooltipOptions, Point } from 'highcharts'
import { SeriesColumnRange } from 'teacher-ui-client/components/giraffe/primitives/series-column-range'

export interface LexileGrowthByGradeReportData {
  grades: {
    grade_position: number
    from_lexile_growth: number
    to_lexile_growth: number
    student_count: number
  }[]
  student_count: number
}

interface Args {
  data: LexileGrowthByGradeReportData
}

interface Signature {
  Element: HTMLDivElement
  Args: Args
}

export class DashboardLexileGrowth extends Component<Signature> {
  @service gradeSets!: GradeSetsService

  dataLabelsFormatter(this: Point) {
    /* below isn't something we set and is not part of Point interface, but it is set by datalabels and we use it to
     * only render the 'above' datalabel (not below) */
    const { low, high, below } = this as Point & { low: number; high: number; below: boolean }
    const growth = high - low
    return below ? '' : `+${growth}`
  }

  get sortedGrades() {
    return this.args.data.grades.slice().sort((a, b) => a.grade_position - b.grade_position) ?? []
  }

  get chartData(): SeriesColumnrangeOptions[] {
    const data = this.sortedGrades.map((gradeInfo) => {
      const { from_lexile_growth: lexileFrom, to_lexile_growth: lexileTo, student_count: studentCount } = gradeInfo

      if ([studentCount, lexileFrom, lexileTo].every(isFinite)) {
        const avgMinLexile = Math.round(lexileFrom)
        const avgMaxLexile = Math.round(lexileTo)

        return [avgMinLexile, avgMaxLexile]
      }
      return [0, 0]
    })

    return [{ data, color: colors.oceanyBlue300, type: 'columnrange' as const }]
  }

  get subtitleKey(): string {
    if (this.singleGrade) return 'reporting.dashboardSubtitles.singleGrade.lexileGrowth'
    return 'reporting.dashboardSubtitles.multiGrade'
  }

  get singleGrade(): boolean {
    return this.args.data.grades.length === 1
  }

  get categories(): string[] {
    return this.sortedGrades.map((grade) => this.gradeSets.findByPosition(grade.grade_position)?.name ?? '')
  }

  get studentCount(): string {
    const count = this.args.data.student_count ?? 0
    return formatNumber(count, 2)
  }

  get toolTip(): TooltipOptions {
    return toolTipConfig('y', '', {
      formatter(this: Point) {
        const formatPoint = this as Point & { low: number; high: number }
        if (formatPoint.low === formatPoint.high) {
          return `<b>${formatPoint.high}L</b>`
        }
        return `<b>From ${formatPoint.low}L to ${formatPoint.high}L</b>`
      },
      backgroundColor: colors.neutral400,
    })
  }

  <template>
    <div data-test-giraffe-dashboard-lexile-growth class="flex h-full flex-col" ...attributes>
      <div class="flex justify-between">
        <p data-test-dashboard-subtitle-key class="text-dusty-black-500 mb-3 pr-[5px] pl-0 text-xs">
          {{t this.subtitleKey}}
        </p>
        <p class="text-dusty-black-500 mb-3 pr-0 pl-[5px] text-right text-xs">
          {{t "reporting.dashboardSubtitles.studentCount" count=this.studentCount}}
        </p>
      </div>
      <div class="relative grow">
        <div class="absolute inset-0">
          <SeriesColumnRange
            @categories={{this.categories}}
            @data={{this.chartData}}
            @toolTip={{this.toolTip}}
            @xAxisLabel={{t "grade.label"}}
            @yAxisFloor={{0}}
            @dataLabelsEnabled={{true}}
            @dataLabelsFormatter={{this.dataLabelsFormatter}}
          />
        </div>
      </div>
    </div>
  </template>
}

export default DashboardLexileGrowth
